import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, MenuItem, Paper, TextField } from "@mui/material";
import type { RegisterSelection } from "../api/spofRegister";
import type { RegisterFilterOptions } from "../types";

const SELECTORS = [
  { key: "region", label: "Region", empty: "All Regions" },
  { key: "power_plant_id", label: "Plant", empty: "All Plants" },
  { key: "unit", label: "Unit", empty: "All Units" },
  { key: "system", label: "System", empty: "All Systems" },
  { key: "risk_category", label: "Current Risk", empty: "All Risks" },
] as const;

function values(
  options: RegisterFilterOptions,
  selection: RegisterSelection,
  key: (typeof SELECTORS)[number]["key"],
) {
  if (key === "power_plant_id") {
    return options.plants
      .filter((plant) => !selection.region || plant.region === selection.region)
      .map((plant) => ({ id: plant.id, label: plant.name }));
  }
  const list =
    key === "region"
      ? options.regions
      : key === "unit"
        ? options.units
        : key === "system"
          ? options.systems
          : options.risk_categories;
  return list.map((value) => ({ id: value, label: value }));
}

export default function SpofRegisterFilters({
  options,
  selection,
  onChange,
}: {
  options: RegisterFilterOptions;
  selection: RegisterSelection;
  onChange: (selection: RegisterSelection) => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          lg: "repeat(6, 1fr)",
        },
      }}
    >
      {SELECTORS.map(({ key, label, empty }) => (
        <TextField
          key={key}
          select
          size="small"
          label={label}
          value={selection[key] ?? ""}
          onChange={(event) =>
            onChange({ ...selection, [key]: event.target.value || null })
          }
          slotProps={{
            select: { displayEmpty: true },
            inputLabel: { shrink: true },
          }}
        >
          <MenuItem value="">{empty}</MenuItem>
          {values(options, selection, key).map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ))}

      <TextField
        size="small"
        label="Keyword"
        placeholder="SPOF ID or functional location"
        value={selection.keyword ?? ""}
        onChange={(event) =>
          onChange({ ...selection, keyword: event.target.value || null })
        }
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          },
        }}
      />
    </Paper>
  );
}
